/**
 * Tema da landing de captação — derivado do guia de marca oficial
 * (guia_marca_twms.zip, em doc-storage).
 *
 * O "Cores site de captação.pdf" restringe a landing a TRÊS cores e mostra
 * explicitamente quais combinações de fundo/texto são aprovadas. Por isso o
 * editor não expõe color picker livre: só os combos abaixo. Assim é impossível
 * montar uma seção fora do guia.
 *
 * Atenção: a paleta usada em SejaConsultora.tsx (promo 80 anos, #FF611F etc.)
 * é outra e NÃO vale aqui.
 */

/** As três cores do site de captação, com o Pantone correspondente. */
export const COLORS = {
  marrom:  '#613400', // PMS 724 C
  laranja: '#FF7500', // PMS 1585 C
  creme:   '#FFE4AB', // PMS 148 C
} as const

export type ColorName = keyof typeof COLORS

/** Nome do combo de cor escolhido por bloco — é o fundo da seção. */
export type ComboName = ColorName

export interface Combo {
  /** Fundo da seção. */
  bg: string
  /** Cor de texto padrão sobre esse fundo. */
  fg: string
  /** Segunda cor de texto aprovada sobre esse fundo (só o marrom tem duas). */
  fgAlt?: string
  /** Fundo/texto do botão de CTA, também dentro dos pares aprovados. */
  ctaBg: string
  ctaFg: string
}

/**
 * Pares aprovados no guia:
 *   fundo marrom  → texto laranja OU creme
 *   fundo laranja → texto marrom
 *   fundo creme   → texto marrom
 * Nenhuma outra combinação aparece no material, então nenhuma outra existe aqui.
 */
export const COMBOS: Record<ComboName, Combo> = {
  marrom: {
    bg: COLORS.marrom,
    fg: COLORS.creme,
    fgAlt: COLORS.laranja,
    ctaBg: COLORS.laranja,
    ctaFg: COLORS.marrom,
  },
  laranja: {
    bg: COLORS.laranja,
    fg: COLORS.marrom,
    ctaBg: COLORS.marrom,
    ctaFg: COLORS.creme,
  },
  creme: {
    bg: COLORS.creme,
    fg: COLORS.marrom,
    ctaBg: COLORS.laranja,
    ctaFg: COLORS.marrom,
  },
}

/**
 * Superfície de card sobre cada fundo. Cartão branco translúcido some sobre o
 * creme, então cada fundo ganha uma cor sólida — sempre um par aprovado:
 * creme → branco, marrom e laranja → creme, os dois com texto marrom.
 */
export const CARD_SURFACE: Record<ComboName, { bg: string; fg: string }> = {
  marrom:  { bg: COLORS.creme, fg: COLORS.marrom },
  laranja: { bg: COLORS.creme, fg: COLORS.marrom },
  creme:   { bg: '#FFFFFF',    fg: COLORS.marrom },
}

/** Rótulos em português para os swatches do editor. */
export const COMBO_LABELS: Record<ComboName, string> = {
  marrom:  'Marrom',
  laranja: 'Laranja',
  creme:   'Creme',
}

/**
 * Resolve fundo e texto de um bloco. `textOn` só tem efeito no fundo marrom,
 * o único com duas cores de texto aprovadas.
 */
export function resolveCombo(combo: ComboName, textOn?: ColorName): Combo {
  const base = COMBOS[combo]
  if (textOn && base.fgAlt && COLORS[textOn] === base.fgAlt) {
    return { ...base, fg: base.fgAlt, fgAlt: base.fg }
  }
  return base
}

// ─── Tipografia ────────────────────────────────────────────────────────────
// Guia: Título = Earl Sans Bold · Corpo = Earl Sans Light · Legenda = Bold.
// Fallback oficial quando a Earl Sans não estiver disponível: Work Sans.
// @font-face fica em src/index.css, junto com as demais fontes self-hosted.

export const FONT = "'Earl Sans', 'Work Sans', system-ui, -apple-system, sans-serif"

export const WEIGHT = {
  corpo:   300,
  titulo:  700,
  display: 900,
} as const

// ─── Espaçamento vertical ──────────────────────────────────────────────────

export type PadName = 'compact' | 'normal' | 'amplo'

/** Padding vertical da seção. clamp() para encolher no mobile sem media query. */
export const PAD: Record<PadName, string> = {
  compact: 'clamp(20px, 3vw, 32px)',
  normal:  'clamp(56px, 8vw, 96px)',
  amplo:   'clamp(72px, 11vw, 132px)',
}

export const PAD_LABELS: Record<PadName, string> = {
  compact: 'Compacto',
  normal:  'Normal',
  amplo:   'Amplo',
}

// ─── Aparência de um campo isolado ─────────────────────────────────────────
// Os controles que o painel oferece campo a campo. Todos com opções fechadas:
// nenhum aceita valor livre, então o guia continua sendo cumprido por
// construção — a mesma razão pela qual não existe color picker aqui.

/** Rótulos do controle de alinhamento dos campos que o declaram no blueprint. */
export const ALIGN_CAMPO_LABELS = {
  left:   'Esquerda',
  center: 'Centro',
  right:  'Direita',
} as const

export const ALIGN_CAMPO_CLS = {
  left:   'text-left',
  center: 'text-center',
  right:  'text-right',
} as const

/** Limites do campo de tamanho. Fora disso é engano de digitação, não escolha. */
export const TAM_MIN = 8
export const TAM_MAX = 200

/**
 * Tamanho da fonte a partir do número escolhido no painel.
 *
 * O número é o tamanho na tela grande. Em tela estreita ele encolhe, como todo
 * o resto da landing já faz: um título de 38px fixo ocupa um terço da altura de
 * um celular e quebra em cinco linhas. A fórmula reproduz a proporção dos
 * clamps que já estavam escritos à mão no tema (viewport de referência ~1100px,
 * piso em 72% do valor).
 */
export function tamanhoFluido(px: number): string {
  const alvo = Math.min(TAM_MAX, Math.max(TAM_MIN, Math.round(px)))
  const piso = Math.max(TAM_MIN, Math.round(alvo * 0.72))
  return `clamp(${piso}px, ${(alvo / 11).toFixed(2)}vw, ${alvo}px)`
}

/**
 * Peso do texto. A família é sempre a Earl Sans — o guia não prevê outra na
 * landing —, então o que varia é o papel tipográfico: Corpo = Light,
 * Título = Bold, Display = Black. São exatamente as chaves de WEIGHT.
 */
export type PesoCampo = keyof typeof WEIGHT

export const PESO_CAMPO_LABELS: Record<PesoCampo, string> = {
  corpo:   'Corpo',
  titulo:  'Título',
  display: 'Display',
}

/**
 * Entrelinha. Título quer apertado, parágrafo quer solto — o valor fixo de 1,25
 * do `Txt` servia bem para um e mal para o outro.
 */
export type EntrelinhaCampo = 'apertada' | 'normal' | 'solta'

export const ENTRELINHA: Record<EntrelinhaCampo, number> = {
  apertada: 1.1,
  normal:   1.4,
  solta:    1.8,
}

export const ENTRELINHA_LABELS: Record<EntrelinhaCampo, string> = {
  apertada: 'Entrelinha apertada',
  normal:   'Entrelinha normal',
  solta:    'Entrelinha solta',
}

/** Espaçamento entre letras. O guia usa caixa alta espaçada nos rótulos. */
export type EspacamentoCampo = 'apertado' | 'normal' | 'solto'

export const ESPACAMENTO: Record<EspacamentoCampo, string> = {
  apertado: '-0.02em',
  normal:   '0',
  solto:    '0.14em',
}

export const ESPACAMENTO_LABELS: Record<EspacamentoCampo, string> = {
  apertado: 'Letras apertadas',
  normal:   'Letras normais',
  solto:    'Letras espaçadas',
}

/**
 * Cores oferecidas primeiro na barra de ferramentas. As três da marca mais o
 * branco, que o guia usa em cartão sobre fundo creme. A barra também aceita cor
 * livre; estas ficam à frente para que o caminho fácil continue sendo o certo.
 */
export const CORES_MARCA: { valor: string; label: string }[] = [
  { valor: COLORS.marrom,  label: 'Marrom' },
  { valor: COLORS.laranja, label: 'Laranja' },
  { valor: COLORS.creme,   label: 'Creme' },
  { valor: '#FFFFFF',      label: 'Branco' },
]

// ─── Profundidade, vidro e textura ─────────────────────────────────────────
// O CRM tem `.glass-panel` e `.card-shadow` em index.css, mas os dois carregam
// sombra `rgba(13,39,80,…)` — azul-marinho — e dependem dos tokens de tema, que
// a landing não monta. As receitas abaixo são as mesmas ideias reescritas em
// cor de marca, sem depender de nada do painel.

/** Sombras derivadas do marrom, não do azul do painel. */
export const SHADOW = {
  card:  '0 1px 3px rgba(97,52,0,0.10), 0 14px 30px -12px rgba(97,52,0,0.28)',
  alto:  '0 2px 6px rgba(97,52,0,0.12), 0 26px 50px -18px rgba(97,52,0,0.38)',
  botao: '0 1px 2px rgba(97,52,0,0.18), 0 6px 16px -6px rgba(97,52,0,0.42)',
} as const

/**
 * Vidro fosco em tint de marca — a receita do `.glass-bar` do CRM
 * (translúcido + backdrop-filter + hairline especular) com os valores trocados.
 * Usado no header quando ele descola do topo.
 */
export function vidro(combo: ComboName) {
  const c = COMBOS[combo]
  return {
    backgroundColor: `color-mix(in srgb, ${c.bg} 82%, transparent)`,
    backdropFilter: 'blur(14px) saturate(1.4)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.4)',
    boxShadow: `inset 0 -1px 0 color-mix(in srgb, ${COLORS.marrom} 14%, transparent), 0 10px 30px -14px rgba(97,52,0,0.35)`,
  } as const
}

/**
 * Padrão de bolinhas para o fundo das seções.
 *
 * Substitui a marca d'água com o "T" da Tupperware: o símbolo não pode ser
 * usado como estampa repetida. A malha de pontos é geometria neutra, sem carga
 * de marca, e mantém a função — quebrar o campo de cor chapada.
 *
 * Sai de um radial-gradient, então acompanha a cor de texto do bloco e repete
 * reto em qualquer proporção. O espaçamento vem do backgroundSize.
 */
export function pontos(cor: string): string {
  // `circle` explícito: sem isso o padrão é `ellipse`, que se deforma junto
  // com o ladrilho. A transição de 1,4 a 2px evita o ponto serrilhado.
  return `radial-gradient(circle, ${cor} 1.4px, transparent 2px)`
}


// ─── Sistema de formas ─────────────────────────────────────────────────────

/**
 * O guia (p28-29) define contêineres angulados a 42,5° / 47,5° para segurar
 * CTA, imagens e texto. Aqui vira um clip-path opcional por bloco — corta só a
 * borda escolhida, mantendo o retângulo no resto.
 */
export const SHAPE_ANGLE = { a: 42.5, b: 47.5 } as const

export type ShapeName = 'none' | 'topo' | 'base' | 'ambos'

export function shapeClip(shape: ShapeName): string | undefined {
  // tan(47,5°) ≈ 1,09 — a altura do corte sai de uma fatia de 4vw de largura.
  const cut = 'clamp(18px, 4vw, 56px)'
  switch (shape) {
    case 'topo':  return `polygon(0 ${cut}, 100% 0, 100% 100%, 0 100%)`
    case 'base':  return `polygon(0 0, 100% 0, 100% calc(100% - ${cut}), 0 100%)`
    case 'ambos': return `polygon(0 ${cut}, 100% 0, 100% calc(100% - ${cut}), 0 100%)`
    default:      return undefined
  }
}

export const SHAPE_LABELS: Record<ShapeName, string> = {
  none:  'Reto',
  topo:  'Angulado em cima',
  base:  'Angulado embaixo',
  ambos: 'Angulado nos dois',
}

// ─── Imagem de fundo da lâmina ─────────────────────────────────────────────

/**
 * Ponto focal do recorte `cover`, em português de quem não edita foto.
 *
 * "Ponto focal" é o nome técnico; a pergunta que a pessoa tem na cabeça é
 * literalmente "que parte da foto aparece", porque `cover` corta o resto.
 *
 * A união de `BlockStyle['fundoPos']` repete estas chaves. É duplicação de
 * propósito: tipar lá a partir daqui inverteria a dependência — é o blueprint
 * que importa o tema, nunca o contrário — para poupar cinco literais.
 */
export const FUNDO_POS_LABELS = {
  center: 'Centro da foto',
  top:    'Parte de cima',
  bottom: 'Parte de baixo',
  left:   'Lado esquerdo',
  right:  'Lado direito',
} as const

/**
 * "Escurecer" em rótulos, não em número.
 *
 * O véu é a opacidade de uma camada na COR DE FUNDO APROVADA da seção (ver
 * `Section` em kit.tsx) — velar com preto injetaria uma quarta cor na página.
 * O controle era um `number` de passo 0,05 rotulado "Véu sobre o fundo (0 = foto
 * crua, 1 = cor sólida)". Ninguém que não seja da área acerta isso na primeira,
 * e errar deixa a seção ilegível ou a foto invisível.
 */
export const VEU_PRESETS = [
  { label: 'Nada',  valor: 0 },
  { label: 'Pouco', valor: 0.2 },
  { label: 'Médio', valor: 0.4 },
  { label: 'Muito', valor: 0.65 },
] as const

/**
 * Folga para acender o botão certo.
 *
 * Um véu salvo pelo campo numérico antigo (o hero tem 0,3 gravado) não bate
 * exato com preset nenhum, e com igualdade estrita a barra abriria com NADA
 * aceso — parecendo que o controle não sabe o próprio estado.
 */
export const VEU_TOLERANCIA = 0.06

/**
 * Véu que uma foto de fundo RECÉM-ENVIADA ganha de brinde.
 *
 * Foto crua atrás de texto costuma matar o contraste, e quem "só quis pôr uma
 * foto" não vai caçar o controle de véu depois — vai concluir que a seção ficou
 * ilegível e que o recurso não presta. Só vale na PRIMEIRA imagem: trocar a foto
 * preserva o que já foi escolhido, inclusive "Nada" escolhido de propósito.
 */
export const VEU_PADRAO = 0.4
